import {useRouter} from '@tanstack/react-router';
import {useEffect, useState, useSyncExternalStore} from 'react';

import {getEmbeddedBottomChrome, subscribeEmbeddedBottomChrome} from 'src/embed/bottomChrome';

const GAP_ABOVE_FOOTER = 8;
const GAP_WITHOUT_FOOTER = 24;
const LOOKUP_RETRY_MS = 250;
const LOOKUP_MAX_RETRIES = 40;
const FULLSCREEN_DIALOG_SELECTOR = '.MuiDialog-paperFullScreen';

type LayoutElementScope = 'page-or-dialog' | 'page-only';

function findLayoutElement(selector: string, scope: LayoutElementScope): Element | null {
  const fullscreenDialog = document.querySelector(FULLSCREEN_DIALOG_SELECTOR);
  if (!fullscreenDialog) {
    return document.querySelector(selector);
  }
  return scope === 'page-or-dialog' ? fullscreenDialog.querySelector(selector) : null;
}

function isSameRect(a: DOMRect | null, b: DOMRect | null): boolean {
  if (!a || !b) {
    return a === b;
  }
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function useMeasuredElement(selector: string, scope: LayoutElementScope, resetKey: string): DOMRect | null {
  const [rect, setRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    let resizeObserver: ResizeObserver | undefined;
    let retryTimeout = 0;
    let retries = 0;

    const update = (next: DOMRect | null) => setRect((current) => (isSameRect(current, next) ? current : next));

    const resolve = () => {
      resizeObserver?.disconnect();
      resizeObserver = undefined;
      const element = findLayoutElement(selector, scope);
      if (!element) {
        update(null);
        if (retries < LOOKUP_MAX_RETRIES) {
          retries += 1;
          retryTimeout = window.setTimeout(resolve, LOOKUP_RETRY_MS);
        }
        return;
      }
      const measure = () => update(element.getBoundingClientRect());
      measure();
      resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(element);
    };

    const mutationObserver = new MutationObserver(() => {
      window.clearTimeout(retryTimeout);
      retries = 0;
      resolve();
    });
    mutationObserver.observe(document.body, {childList: true});
    resolve();

    return () => {
      window.clearTimeout(retryTimeout);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, [selector, scope, resetKey]);

  return rect;
}

export function useFloatingBottomOffset(): number {
  const router = useRouter();
  const footer = useMeasuredElement('footer', 'page-or-dialog', router.state.location.pathname);
  const embeddedBottomChrome = useSyncExternalStore(subscribeEmbeddedBottomChrome, getEmbeddedBottomChrome, () => 0);
  const pageOffset = footer ? Math.round(footer.height) + GAP_ABOVE_FOOTER : GAP_WITHOUT_FOOTER;

  return Math.max(pageOffset, embeddedBottomChrome ? embeddedBottomChrome + GAP_ABOVE_FOOTER : 0);
}

export function useLayoutNavWidth(): number {
  const router = useRouter();
  const nav = useMeasuredElement('nav', 'page-only', router.state.location.pathname);
  return nav ? Math.round(nav.width) : 0;
}
